import { JwtTokenFullData } from 'multirpc-sdk';
import { createSelector } from 'reselect';

import { MultiService } from 'modules/api/MultiService';
import { TFAEndpointMeta, TFAQueryFnParams } from 'modules/tfa/types';
import { createQueryFnWithErrorHandler } from 'store/utils/createQueryFnWithErrorHandler';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { decryptJWT } from 'modules/common/utils/decryptJWT';
import { isTFAError } from 'modules/tfa/utils/isTFAError';
import { web3Api } from 'store/queries';

export interface IFetchDevdaoAccountJWTParams
  extends TFAQueryFnParams<unknown> {}

export const fetchDevdaoAccountJWTFallback: JwtTokenFullData = {};

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be refelected there as well.
export const {
  endpoints: { fetchDevdaoAccountJWT },
  useFetchDevdaoAccountJWTQuery,
  useLazyFetchDevdaoAccountJWTQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchDevdaoAccountJWT: build.query<
      JwtTokenFullData,
      IFetchDevdaoAccountJWTParams
    >({
      queryFn: createQueryFnWithErrorHandler({
        queryFn: async ({ totp }) => {
          const api = MultiService.getService().getAccountingGateway();

          const { is_encrypted: isEncrypted, jwt_data: jwt } =
            await api.getOrCreateDevdaoInstantJwt(totp);

          const decryptedJWT = await decryptJWT({ isEncrypted, jwt });

          return { data: decryptedJWT };
        },
        errorHandler: error => {
          const meta: TFAEndpointMeta = {
            shouldSignOutOnTFAError: true,
          };

          return { error, meta };
        },
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  selectDataWithFallbackCachedByParams: selectDevdaoAccountJWT,
  selectStateCachedByParams: selectDevdaoAccountJWTState,
} = createQuerySelectors({
  endpoint: fetchDevdaoAccountJWT,
  fallback: fetchDevdaoAccountJWTFallback,
});

export const selectDevdaoAccountJWTLoading = createSelector(
  selectDevdaoAccountJWTState,
  ({ error, isLoading }) => isLoading || isTFAError(error),
);
